import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import ModalFlow from 'components/ModalFlow'
import Button from 'components/Button'
import Type from 'components/Type'
import Label from 'components/Label'
import TextArea from 'components/TextArea'
import A from 'components/A'
import Input from 'components/Input'
import Currency from 'components/Currency'
import IconButton from 'components/IconButton'
import InfoButton from 'components/InfoButton'
import TextButton from 'components/TextButton'
import Icon from 'components/Icon'
import Paper from 'components/Paper'
import CloudinaryUpload from 'components/CloudinaryUpload'
import CircleFrame from 'components/CommonUI/CircleFrame'
import IconList from 'components/CommonUI/IconList'
import InlineButton from 'components/CommonUI/InlineButton'
import DashedBoxButton from 'components/CommonUI/DashedBoxButton'
import ExternalAccounts from 'components/CommonUI/ExternalAccounts'
import AllSet from 'components/ModalViews/AllSet'
import CreateVirtualCard from 'components/ModalViews/CreateVirtualCard'
import { 
  REQUEST_REIMBURSEMENT, 
} from 'api/constants'
import { useTypeLoad } from 'utils/hooks'
import { cloneDeep, findIndex } from 'lodash'
import { useSavingsTotals, useAction } from 'utils/actions'
import { addCSImages, setCSImages } from 'slices/customerSupportSlice'

const RequestReimbursement = () => {
  const dispatch = useDispatch()
  const {
    bank_actual_total,
  } = useSavingsTotals()
  const {
    openDrawer,
  } = useAction()
  const csImages = useSelector(({ customerSupport }) => customerSupport.csImages) || []
  const [activeIndex, setActiveIndex] = useState(0)
  const [description, setDescription] = useState('')
  const [vendor, setVendor] = useState('')
  const [amount, setAmount] = useState('')
  const [account, setAccount] = useState(null)
  const isDisabled = 
    !account ||
    !+amount ||
    +amount > +bank_actual_total ||
    !vendor.length ||
    !description.length ||
    !csImages?.length

  const onSendRequest = () => {
    setActiveIndex(-1)
    dispatch({
      type: REQUEST_REIMBURSEMENT,
      data: {
        vendor,
        description,
        amount,
        to_account: account?.name,
        to_account_id: account?.id,
        attachment_data: {
          images: csImages,
        },
      },
    })
  }

  const handleOnUploadSuccess = imageArray => {
    if (imageArray?.length) {
      dispatch(addCSImages(imageArray.map(o => o.info)))
    }
  }

  const onCreateVC = () => {
    openDrawer(<CreateVirtualCard />)
  }

  const handleRemoveImage = image => {
    const temp = cloneDeep(csImages)
    temp.splice(findIndex(csImages, { secure_url: image.secure_url }), 1)
    dispatch(setCSImages(temp))
  }

  useTypeLoad(REQUEST_REIMBURSEMENT, data => {
    if (!data.error) {
      setActiveIndex(1)
      dispatch(setCSImages([]))
    }
  })

  return (
    <ModalFlow  
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      sizeX="md"
      loaderLabel="Submitting Request"
    >
      {/* NOTICE */}
      <Stack
        px={3}
      >
        <CircleFrame
          mx="auto"
          mb={2}
        >
          <Icon
            icon="CelInfo"
            width="50"
            height="50"
          />
        </CircleFrame>
        <div>
          <Type
            variant="data"
            mb={2}
          >
            Travel expenses are only eligible for reimbursement of company contributions in the following circumstances:
          </Type>
          <IconList
            icon="SuccessSmallDuo"
            align="flex-start"
            gap={1}
            mb={2}
          >
            <Type 
              variant="data"
            >
              Pay-on-arrival hotel reservations
            </Type>
            <Type 
              variant="data"
            >
              Pay-on-arrival car rentals
            </Type>
            <Type 
              variant="data"
            >
              Preapproval by the support team
            </Type>
          </IconList>
          <Type
            variant="data"
            mb={4}
          >
            Approved reimbursements are processed one business day post-travel. For all other purchases outside of Dónde, <InlineButton color="tertiary" onClick={onCreateVC}>create a virtual payment card</InlineButton>.
          </Type>
          <Button
            justifyContent="center"
            onClick={() => setActiveIndex(1)}
          >
            I understand, continue
          </Button>
        </div>
      </Stack>
      {/* ADDITIONAL INFO */}
      <Stack
        display="grid"
        p={3}
      >
        <CircleFrame
          justifySelf="center"
          mb={2}
        >
          <Icon
            icon="CelUploadReceipt"
          />
        </CircleFrame>
        <Type
          variant="sectionLabel"
          textAlign="center"
          mb={1.5}
        >
          Reimburse an expense
        </Type>
        <Stack
          sx={theme => theme.internalMixins.columns({ gap: .5, justify: 'center' })}
          mb={3}
        >
          <Type
            variant="chipLabel"
            textAlign="center"
          >
            Total savings on Dónde:
            {' '}
            <Type
              variant="inherit"
              color="tertiary"
              inline
              fontWeight="regular"
            >
              <Currency 
                amount={bank_actual_total} 
                noDecimal="conditional"
              />
            </Type>
          </Type>
          <InfoButton>
            <Type 
              variant="labelMicroMedium"
            >
              Pending savings activity may result in a discrepancy in your savings balance here and as reported elsewhere on Dónde. Dónde Points cannot be applied to reimbursements
            </Type>
          </InfoButton>
        </Stack>
        <Stack
          gap={2}
          mb={3}
        >
          <Input
            label="Vendor Name"
            value={vendor}
            onChange={setVendor}
          />
          <Stack
            gap={1}
          >
            <Label>
              Description
            </Label>
            <TextArea
              fluid
              noResize
              size="short"
              charCount={100}
              value={description}
              onChange={setDescription}
            />
            <Input
              icon="$"
              iconColor="mainCool"
              label="Amount"
              mask="currency_no_prefix"
              removeMaskOnChange
              value={amount}
              onChange={setAmount}
            />
          </Stack>
          <ExternalAccounts
            label="to"
            showAddAccount
            onSetAccount={setAccount}
          />
          <Stack
            gap={1}
          >
            <Label>
              Photo of Receipt
            </Label>
            <CloudinaryUpload
              id="customer-service-upload"
              preset="CUSTOMER_SERVICE"
              reopenID="customer-service-upload"
              options="OPTIONS_CUSTOMER_SERVICE"
              dropzoneProps={{ allowBulk: true }}
              onBulkUploadSuccess={data => handleOnUploadSuccess(data, csImages)}
            >
              <DashedBoxButton>
                <Stack
                  sx={theme => theme.internalMixins.columns({ gap: 1 })}
                  py={1.5}
                  px={2}
                >
                  <Icon 
                    icon="UploadPhotos"
                    width="40"
                    height="40"
                  />
                  <div>
                    <Type color="neutralCool_700" variant="p">
                      Drop your files here or{' '}
                      <TextButton
                        variant="buttonLarge"
                        inline
                        color="tertiary"
                      >
                        browse for a file
                      </TextButton>
                    </Type>
                  </div>
                </Stack>
              </DashedBoxButton>
            </CloudinaryUpload>
          </Stack>
          {!!csImages?.length && (
            <Stack
              direction="row"
              gap={2}
              flexWrap="wrap"
            >
              {csImages?.map(o => (
                <Box
                  key={o.secure_url}
                  sx={{
                    position: 'relative',
                    width: 75,
                    height: 75,
                    background: `url(${o.secure_url})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <IconButton
                    as={Box}
                    solid
                    position="absolute"
                    top="-10px"
                    right="-10px"
                    zIndex="primary"
                    size="sm"
                    depth={3}
                    variant="inverse"
                    variantSolidHover="inverse"
                    color="inverse"
                    onClick={() => handleRemoveImage(o)}
                  >
                    <Icon icon="Close" />
                  </IconButton>
                  {o.secure_url?.endsWith('.pdf') && (
                    <Icon
                      icon="CelPDF"
                      width="75"
                      height="75"
                    />
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
        <Button
          justifyContent="center"
          disabled={isDisabled}
          onClick={onSendRequest}
        >
          Submit Request
        </Button>
        <Type
          variant="labelSubAtomicMedium"
          color="text.neutral_500"
          textAlign="center"
          mt={1.5}
        >
          By clicking ‘Submit Request’ you authorize a one-time transfer between your Dónde Wallet and the selected account above. Your authorization is effective immediately. You can cancel your request before it begins processing by contacting us at (213) 463-6633.
        </Type>
      </Stack>
      {/* ALL SET */}
      <AllSet 
        title="Your reimbursement has been submitted!"
        subtitle="We’re currently reviewing your submission. You should see your reimbursement appear in your selected account within 5–10 business days."
        content={(
          <Paper
            round="more"
            sx={{
              background: 'linear-gradient(315deg, #DAF3F5 0%, #F3F5F7 100%)',
            }}
          >
            <Stack
              display="grid"
              gap={1}
              justifyContent="center"
              justifyItems="center"
              py={3}
              px={1.5}
              pb={2}
              textAlign="center"
            >
              <Icon
                icon="CelSendVirtualCard"
              />
              <Type
                variant="labelSmall"
                color="text.secondary"
              >
                Did you know you can book travel anywhere online using your Dónde funds with a virtual card?
              </Type>
              <A
                to="/wallet/virtual-card"
                wrapComponent
              >
                <TextButton
                  color="tertiary"
                >
                  Learn more about virtual cards
                </TextButton>
              </A>
            </Stack>
          </Paper>
        )}
        cancelTimeout
      />
    </ModalFlow>
  )
}

RequestReimbursement.defaultProps = {
  drawerTitle: 'Reimburse a Travel Purchase',
}

export default RequestReimbursement